import {
  Box,
  Button,
  Field,
  Input,
  InputGroup,
  Select,
  Stack,
  Flex,
  VStack,
  createListCollection,
  FormatNumber,
  Textarea,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { CopyIcon, toaster, tools } from '@halo/ui-chat-react';
import type { DerivedWalletId } from '@halo/wallet-app';
import { BIF, type DerivedWallet, type CoinState } from '@halo/wallet-app';
import { useDeferredValue } from 'react';
import { useTranslation } from 'react-i18next';
import { useWalletStore } from '@/wallet';
import WarningIcon from '@/assets/images/common/svg/warning.svg?react';
import SelectToken from '../SelectToken';
import { GasFee } from './GasFee';
import { useGasFee } from './useGasFee';

export interface TransferFormValues {
  myAccount: string;
  amount: DerivedWalletId;
  message?: string;
  chainType: string;
  account: DerivedWallet;
  coin: CoinState;
  from: string;
  to: string;
  gasFeeResult: any;
}

interface LuckyFormProps {
  toAddresses?: Record<string, string>;
  setToAddress: (address: string) => void;
  onConfirm: (data: TransferFormValues) => void;
}

export function TransferForm({ toAddresses, setToAddress, onConfirm }: LuckyFormProps) {
  const { t } = useTranslation();
  const { accounts, getNet } = useWalletStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    trigger,
    control,
  } = useForm<TransferFormValues>();
  const container = useRef<HTMLDivElement>(null);
  const [amount, setAmount] = useState<string>();
  const [myAccount, setMyAccount] = useState<DerivedWalletId>();
  const [coin, setCoin] = useState<CoinState>();
  const [gasFeeTrigger, setGasFeeTrigger] = useState<object>();

  const message = watch('message');

  const amountVal = useDeferredValue(amount);

  const accountsCollection = useMemo(() => {
    return createListCollection({
      items: accounts?.flatMap(({ derivedWallets, ...restAccount }) => {
        return derivedWallets.map((item) => ({
          ...restAccount,
          pid: restAccount.id,
          pName: restAccount.name,
          label: item.name,
          value: item.id,
          ...item,
        }));
      }),
    });
  }, [accounts]);

  const currentAccount = useMemo(() => {
    return accountsCollection.items.find((account) => account.value === myAccount);
  }, [accountsCollection, myAccount]);

  const currentNet = useMemo(() => {
    if (coin?.netId) return getNet(coin?.netId);
  }, [coin?.netId, getNet]);

  const to = useMemo(() => {
    if (coin?.chainType) return toAddresses?.[coin?.chainType];
  }, [toAddresses, coin]);

  const useGasFeeResult = useGasFee({
    accountId: myAccount,
    net: currentNet,
    coin,
    to: to || '',
    amount: amountVal || '',
    trigger: gasFeeTrigger,
  });

  const isAmountGtBalance = useMemo(() => {
    return useGasFeeResult.gasInfo && !useGasFeeResult.validateGasFee.validate;
  }, [useGasFeeResult]);

  const onSubmit = handleSubmit((data) => {
    if (isAmountGtBalance) {
      toaster.error({
        title: t('tips.amountSentExceedsBalanceText'),
      });
      return;
    }
    onConfirm({
      ...data,
      coin: coin!,
      account: currentAccount!,
      from: currentAccount!.seriesWallets?.[coin!.chainType]?.walletAddress,
      to: to!,
      gasFeeResult: useGasFeeResult,
    });
  });

  const refSetToAddress = useRef(setToAddress);

  refSetToAddress.current = setToAddress;

  useLayoutEffect(() => {
    setMyAccount(myAccount || accountsCollection.items[0]?.value);
  }, [accountsCollection.items, myAccount]);

  useEffect(() => {
    refSetToAddress.current(to || '');
  }, [to]);

  useEffect(() => {
    if (!myAccount || !amountVal || !coin) {
      setGasFeeTrigger(undefined);
    } else {
      setGasFeeTrigger({});
    }
  }, [myAccount, amountVal, coin]);

  return (
    <form onSubmit={onSubmit}>
      <Box className="LuckyFormContent scrollHover" px="5" pb="5" ref={container}>
        <Stack gap="5" align="flex-start">
          <Field.Root invalid={!!errors.myAccount}>
            <Field.Label>{t('chat.myAccountsText')}</Field.Label>
            <Controller
              control={control}
              name="myAccount"
              render={({ field }) => (
                <Select.Root
                  collection={accountsCollection}
                  cursor="pointer"
                  bg="white"
                  borderRadius="lg"
                  size="lg"
                  {...register(field.name)}
                  value={myAccount ? [myAccount] : undefined}
                  onValueChange={({ value }) => {
                    const val = value?.[0] as DerivedWalletId;
                    setMyAccount(val);
                    field.onChange(val);
                  }}
                >
                  <Select.HiddenSelect />
                  <Select.Control>
                    <Select.Trigger cursor="pointer" borderRadius="lg">
                      <Select.ValueText placeholder={t('chat.selectMyAccountText')} />
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content gap="2" maxH="200px" overflowY="auto">
                      {accountsCollection.items.map((account) => (
                        <Select.Item
                          item={account}
                          key={account.value}
                          p="0"
                          pr="2"
                          borderRadius="lg"
                          borderWidth="1px"
                          borderColor={myAccount === account.id ? 'brand.100' : 'grey.64'}
                          bg="white"
                        >
                          <VStack
                            minW="0"
                            flex="1"
                            p="2"
                            py="1"
                            borderRadius="lg"
                            cursor="pointer"
                            gap="0"
                            alignItems="start"
                          >
                            <Flex alignItems="center" gap="1" lineHeight="normal" w="full">
                              <Box fontSize="sm" fontWeight="semibold" truncate>
                                {account.name}
                              </Box>
                              {!account.isBackupMnemonic && (
                                <Box
                                  flexShrink="0"
                                  borderRadius="xs"
                                  fontSize="xs"
                                  px="1"
                                  bg="rgba(255, 0, 21, 0.10)"
                                  color="red.100"
                                >
                                  {t('tips.noBackup')}
                                </Box>
                              )}
                            </Flex>
                            <Flex
                              alignItems="center"
                              gap="1"
                              fontSize="xs"
                              fontWeight="medium"
                              minW="0"
                              color="fg.muted"
                              w="full"
                              overflow="hidden"
                            >
                              <Box truncate>
                                {currentNet?.chainType &&
                                  account.seriesWallets[currentNet.chainType]?.walletAddress}
                              </Box>
                              <Box
                                cursor="pointer"
                                color="fg.muted"
                                onClick={(e) => {
                                  if (!currentNet?.chainType) return;
                                  e.stopPropagation();
                                  tools.copyToClipboard(
                                    account.seriesWallets[currentNet.chainType]?.walletAddress
                                  );
                                }}
                              >
                                <CopyIcon />
                              </Box>
                            </Flex>
                          </VStack>
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
              )}
            />
          </Field.Root>

          <Field.Root invalid={!!errors.amount}>
            <Field.Label>{t('chat.amountText')}</Field.Label>
            <Box w="full" bg="white" borderRadius="lg" py="1">
              <InputGroup
                isolation="initial"
                paddingInlineEnd="28"
                endElement={
                  <Field.Root>
                    <SelectToken
                      //  {...register('coin', { required: true })}
                      value={coin || undefined}
                      currentAccount={currentAccount}
                      onValueChange={(value) => {
                        setCoin(value);
                      }}
                    ></SelectToken>
                  </Field.Root>
                }
              >
                <Input
                  {...register('amount', {
                    required: t('tips.pleaseEnterAmountText'),
                    pattern: {
                      value: new RegExp(`^([1-9]\\d*|0)(\\.\\d{1,${coin?.decimal}})?$`),
                      message: t('tips.pleaseEnterValidNumericalAmountText'),
                    },
                    validate: (value) => {
                      const minAmount = BIF.from(1).div(
                        BIF.from(1).shiftRight(coin?.decimal || 6)
                      );
                      if (value && BIF.from(value).lt(minAmount)) {
                        return t('tips.minimumAmountNotLessThanText', {
                          amount: minAmount.toString(),
                        });
                      }
                    },
                    onChange: () => trigger('amount'),
                  })}
                  marginInlineStart="1"
                  paddingInlineEnd="0"
                  paddingRight="4.5"
                  placeholder={t('chat.amountText')}
                  variant="subtle"
                  bg="white"
                  borderRadius="lg"
                  size="xl"
                  fontWeight="semibold"
                  onBlur={(e) => setAmount(e.target.value)}
                />
              </InputGroup>
              <Box mt="1" mx="3" borderBottom="1px solid" borderColor="bg.subtle" />
              <InputGroup
                pr="44px"
                endElement={
                  <Flex hidden={!message} fontSize="xs" fontWeight="medium" color="fg">
                    <Box>{message?.length}</Box>/<Box color="fg.muted">50</Box>
                  </Flex>
                }
              >
                <Textarea
                  {...register('message')}
                  resize="none"
                  rows={1}
                  placeholder={t('tips.messageForTransferText')}
                  variant="subtle"
                  maxLength={50}
                  defaultValue={t('chat.bestWishesText')}
                  bg="white"
                  borderRadius="lg"
                  size="xl"
                  border="none"
                  borderColor="transparent"
                />
              </InputGroup>
            </Box>
            <Field.ErrorText>{errors.amount?.message}</Field.ErrorText>
            <Box w="full" fontSize="xs" color="fg.muted" fontWeight="medium" textAlign="right">
              {t('chat.balanceText')}:
              <Box px="1" as="span">
                <FormatNumber
                  value={Number(coin?.balance || 0)}
                  minimumFractionDigits={Number(coin?.decimal || 5)}
                  minimumSignificantDigits={3}
                />
              </Box>
              {coin?.symbol}
            </Box>
            <Box w="full" fontSize="xs" color="fg.muted" fontWeight="medium" textAlign="right">
              <GasFee {...useGasFeeResult} container={container} />
            </Box>
          </Field.Root>
        </Stack>
      </Box>
      <Box className="LuckyFormFooter" p="5">
        {isAmountGtBalance && (
          <Flex alignItems="center" justifyContent="center" gap="1" color="red" my="5">
            <WarningIcon />
            {t('tips.balanceNotSufficientText', {
              symbol: !useGasFeeResult.validateGasFee.validateMainCoin
                ? useGasFeeResult.mainCoin?.symbol
                : !useGasFeeResult.validateGasFee.validateCoin
                  ? coin?.symbol
                  : '',
            })}
          </Flex>
        )}
        {!to && toAddresses && (
          <Flex alignItems="center" justifyContent="center" gap="1" color="red" my="5">
            <WarningIcon />
            {t('tips.noToAddressFoundText', { coinType: coin?.type })}
          </Flex>
        )}
        <Flex justifyContent="flex-end" pt="5">
          <Button
            disabled={!isValid || isAmountGtBalance || useGasFeeResult?.loading || !to}
            type="submit"
            minW="120px"
            borderRadius="xl"
            _disabled={{ bg: 'grey.80', color: 'fg.subtle' }}
          >
            {t('buttons.confirm')}
          </Button>
        </Flex>
      </Box>
    </form>
  );
}
